import apiClient from './api';
import { API_BASE_URL } from '@/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MobileUploadSession {
  token: string;
  status: 'WAITING' | 'UPLOADED' | 'EXPIRED' | 'CANCELLED';
  expiresAt: string;
  mobileUploadPath: string;
}

export interface MobileUploadedFileInfo {
  id: string;
  originalFilename: string;
  contentType: string;
  size: number;
}

export interface MobileUploadSessionStatus {
  token: string;
  status: 'WAITING' | 'UPLOADED' | 'EXPIRED' | 'CANCELLED';
  expiresAt: string;
  files: MobileUploadedFileInfo[];
}

// ─── Authenticated API calls (taskpane) ───────────────────────────────────────

/**
 * Create a new mobile upload session.
 * Uses authenticated apiClient.
 */
export async function createMobileUploadSession(endpoint: string): Promise<MobileUploadSession> {
  const response = await apiClient.post<MobileUploadSession>(endpoint);
  return response.data;
}

/**
 * Poll the session status from the taskpane.
 * Uses authenticated apiClient.
 */
export async function getMobileUploadStatus(
  endpointTemplate: string,
  token: string
): Promise<MobileUploadSessionStatus> {
  const url = endpointTemplate.replace('{token}', token);
  const response = await apiClient.get<MobileUploadSessionStatus>(url);
  return response.data;
}

/**
 * Download an uploaded file as Blob from the taskpane.
 * Uses authenticated apiClient.
 */
export async function downloadMobileUploadedFile(
  endpointTemplate: string,
  token: string,
  fileId: string
): Promise<Blob> {
  const url = endpointTemplate.replace('{token}', token).replace('{fileId}', fileId);
  const response = await apiClient.get(url, { responseType: 'blob' });
  return response.data;
}

/**
 * Cancel/delete a mobile upload session.
 * Uses authenticated apiClient.
 */
export async function cancelMobileUploadSession(
  endpointTemplate: string,
  token: string
): Promise<void> {
  const url = endpointTemplate.replace('{token}', token);
  await apiClient.delete(url);
}

// ─── Public API call (phone upload page) ──────────────────────────────────────

/**
 * Upload a file from the mobile phone.
 * Public endpoint — no user authentication required (token-based).
 * Uses plain fetch to avoid attaching Bearer tokens.
 */
export async function uploadMobileFileFromPhone(
  token: string,
  file: File
): Promise<{ id: string; originalFilename: string; contentType: string; size: number }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/mobile-upload/sessions/${token}/files`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(errorText || `Upload failed with status ${response.status}`);
  }

  return response.json();
}
